import { useEffect, useState } from 'react';

/**
 * Reactive online/offline status for the driver UI. Uses the browser
 * ``online``/``offline`` events plus a lightweight HEAD ping every 30s to
 * catch captive-portal / partial-connectivity scenarios where the browser
 * thinks it's online but the API is unreachable.
 */
const PING_INTERVAL_MS = 30_000;

const pingApi = async () => {
  try {
    const controller = new AbortController();
    const t = setTimeout(() => controller.abort(), 4000);
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/password-policy`, {
      method: 'GET', cache: 'no-store', signal: controller.signal,
    });
    clearTimeout(t);
    return res.ok;
  } catch {
    return false;
  }
};

export default function useOnlineStatus() {
  const [online, setOnline] = useState(
    typeof navigator === 'undefined' ? true : navigator.onLine
  );

  useEffect(() => {
    const bump = (v) => setOnline(v);
    const on = () => bump(true);
    const off = () => bump(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);

    let cancelled = false;
    const tick = async () => {
      // Only ping when the browser thinks we're online — no point pinging
      // if the OS-level connection is definitely down.
      if (!navigator.onLine) { bump(false); return; }
      const reachable = await pingApi();
      if (!cancelled) bump(reachable);
    };
    const id = setInterval(tick, PING_INTERVAL_MS);
    // Initial probe on mount so a stale "online" state is corrected fast.
    tick();

    return () => {
      cancelled = true;
      clearInterval(id);
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);

  return online;
}
